import { useState } from "react";
import { Check, Download, FileText, Image as ImageIcon, Pencil, Trash2, X } from "lucide-react";
import { chatFileUrl, conversationTitle, formatBytes, timeLabel } from "./chatUtils";

function participantsLabel(conversation) {
  const participants = conversation?.participants || [];
  if (!participants.length) return "";
  if (conversation.type === "group") {
    const shortName = (name) => String(name || "").trim().split(/\s+/)[0]?.toLowerCase().replace(/^./, (c) => c.toUpperCase());
    const firstNames = participants.slice(0, 3).map((p) => shortName(p.name)).filter(Boolean).join(" · ");
    const remaining = Math.max(0, participants.length - 3);
    return `${participants.length} participantes${firstNames ? ` · ${firstNames}` : ""}${remaining ? ` · +${remaining}` : ""}`;
  }
  return participants.map((p) => p.name).join(" · ");
}

function Attachment({ file, messageId }) {
  const url = chatFileUrl(file.id);
  const isImage = (file.content_type || "").startsWith("image/");
  return (
    <a
      data-testid={`chat-message-${messageId}-attachment-${file.id}-link`}
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block mt-2 rounded-lg border border-[#DCD7CE] bg-white/80 overflow-hidden hover:border-[#0B2230]/40 transition-colors"
    >
      {isImage ? (
        <div className="bg-[#F4F1EA]">
          <img data-testid={`chat-message-${messageId}-attachment-${file.id}-image`} src={url} alt={file.filename} className="max-h-40 w-full object-contain" />
        </div>
      ) : null}
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-[#0B2230]">
        {isImage ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
        <span className="truncate flex-1">{file.filename}</span>
        <span className="text-[#8C98A0]">{formatBytes(file.size)}</span>
        <Download className="w-3.5 h-3.5" />
      </div>
    </a>
  );
}

export function MessageThread({ conversation, messages, currentUserId, bottomRef, onEditMessage, onDeleteMessage, onDeleteConversation }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  if (!conversation) {
    return (
      <div data-testid="chat-no-conversation-selected" className="flex-1 grid place-items-center text-sm text-[#5C6B73] bg-white">
        Escolha uma conversa para começar.
      </div>
    );
  }
  return (
    <section className="flex-1 min-h-0 min-w-0 flex flex-col bg-white overflow-hidden" data-testid="chat-message-thread-section">
      <div className="px-5 py-4 border-b border-[#DCD7CE] flex items-start justify-between gap-3 min-w-0 overflow-hidden">
        <div className="min-w-0">
          <div data-testid="chat-active-conversation-title" className="font-heading text-lg font-semibold text-[#0B2230] truncate">
            {conversationTitle(conversation, currentUserId)}
          </div>
          <div
            data-testid="chat-active-conversation-participants"
            className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#5C6B73]"
            title={(conversation.participants || []).map((p) => p.name).join(" · ")}
          >
            {participantsLabel(conversation)}
          </div>
        </div>
        {conversation.can_delete && (
          <button
            data-testid="chat-delete-conversation-button"
            onClick={() => onDeleteConversation(conversation)}
            className="w-9 h-9 rounded-lg border border-[#FECACA] text-[#991B1B] grid place-items-center hover:bg-[#FEF2F2] transition-colors"
            aria-label="Apagar conversa"
            title="Apagar conversa"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 bg-[linear-gradient(180deg,#FFFFFF_0%,#F8F5EF_100%)]">
        {messages.length === 0 && (
          <div data-testid="chat-empty-thread-message" className="h-full grid place-items-center text-sm text-[#5C6B73]">
            Envie a primeira mensagem.
          </div>
        )}
        <div className="space-y-3">
          {messages.map((message) => {
            const mine = message.sender_id === currentUserId;
            const isEditing = editingId === message.id;
            return (
              <div key={message.id} data-testid={`chat-message-${message.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] rounded-xl px-3 py-2 shadow-sm ${mine ? "bg-[#0B2230] text-white" : "bg-white border border-[#DCD7CE] text-[#0B2230]"}`}>
                  {!mine && <div className="text-[11px] font-semibold text-[#1E445B] mb-1">{message.sender_name}</div>}
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        data-testid={`chat-message-${message.id}-edit-input`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        className="w-72 max-w-full min-h-[76px] rounded-lg border border-white/30 bg-white text-[#0B2230] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#DBA470]"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`chat-message-${message.id}-cancel-edit-button`}
                          onClick={() => { setEditingId(null); setDraft(""); }}
                          className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center"
                          aria-label="Cancelar edição"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`chat-message-${message.id}-save-edit-button`}
                          onClick={async () => { await onEditMessage(message.id, draft); setEditingId(null); setDraft(""); }}
                          className="w-8 h-8 rounded-lg bg-[#DBA470] hover:bg-[#C58E5B] text-[#0B2230] grid place-items-center"
                          aria-label="Salvar edição"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    message.text && <div data-testid={`chat-message-${message.id}-text`} className="text-sm whitespace-pre-wrap break-words">{message.text}</div>
                  )}
                  {(message.attachments || []).map((file) => <Attachment key={file.id} file={file} messageId={message.id} />)}
                  <div className={`flex items-center justify-between gap-3 text-[10px] mt-1 ${mine ? "text-white/55" : "text-[#8C98A0]"}`}>
                    <span>{timeLabel(message.created_at)}{message.edited ? " · editada" : ""}</span>
                    {mine && !isEditing && (
                      <span className="inline-flex items-center gap-1">
                        <button
                          data-testid={`chat-message-${message.id}-edit-button`}
                          onClick={() => { setEditingId(message.id); setDraft(message.text || ""); }}
                          className="w-6 h-6 rounded-md hover:bg-white/12 grid place-items-center"
                          aria-label="Editar mensagem"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          data-testid={`chat-message-${message.id}-delete-button`}
                          onClick={() => onDeleteMessage(message.id)}
                          className="w-6 h-6 rounded-md hover:bg-white/12 grid place-items-center"
                          aria-label="Apagar mensagem"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>
    </section>
  );
}